import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { RequestStat } from '../../types';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface StatsChartProps {
  stats: RequestStat[];
  type: 'line' | 'bar';
  dataType: 'requests' | 'bandwidth';
  title: string;
  height?: number;
  className?: string;
}

const StatsChart: React.FC<StatsChartProps> = ({
  stats,
  type,
  dataType,
  title,
  height = 300,
  className = ''
}) => {
  // Formater les données pour le graphique
  const formatChartData = () => {
    const labels = stats.map(stat => {
      const date = new Date(stat.date);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    });

    const data = stats.map(stat => 
      dataType === 'requests' ? (stat.requestCount || stat.count) : stat.bandwidth
    );

    const backgroundColor = dataType === 'requests' 
      ? 'rgba(54, 162, 235, 0.5)' 
      : 'rgba(255, 99, 132, 0.5)';

    const borderColor = dataType === 'requests' 
      ? 'rgb(54, 162, 235)' 
      : 'rgb(255, 99, 132)';

    return {
      labels,
      datasets: [
        {
          label: dataType === 'requests' ? 'Nombre de requêtes' : 'Bande passante (bytes)',
          data,
          backgroundColor,
          borderColor,
          borderWidth: 1,
          tension: 0.4
        }
      ]
    };
  };

  // Options du graphique
  const options: ChartOptions<'line' | 'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              if (dataType === 'bandwidth') {
                // Formater la bande passante en KB, MB, GB selon la taille
                const bytes = context.parsed.y;
                if (bytes < 1024) {
                  label += bytes + ' B';
                } else if (bytes < 1024 * 1024) {
                  label += (bytes / 1024).toFixed(2) + ' KB';
                } else if (bytes < 1024 * 1024 * 1024) {
                  label += (bytes / (1024 * 1024)).toFixed(2) + ' MB';
                } else {
                  label += (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
                }
              } else {
                label += context.parsed.y;
              }
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            if (dataType === 'bandwidth') {
              // Formater la bande passante en KB, MB, GB selon la taille
              const bytes = Number(value);
              if (bytes < 1024) {
                return bytes + ' B';
              } else if (bytes < 1024 * 1024) {
                return (bytes / 1024).toFixed(1) + ' KB';
              } else if (bytes < 1024 * 1024 * 1024) {
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
              } else {
                return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
              }
            }
            return value;
          }
        }
      }
    }
  };

  // Si pas de données, afficher un message
  if (!stats || stats.length === 0) {
    return (
      <div className={`stats-chart-empty ${className}`} style={{ height }}>
        <p>Aucune donnée disponible pour ce graphique</p>
        <style>
          {`
            .stats-chart-empty {
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: #f9f9f9;
              border-radius: 8px;
              color: #666;
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div className={`stats-chart ${className}`} style={{ height }}>
      {type === 'line' ? (
        <Line options={options} data={formatChartData()} />
      ) : (
        <Bar options={options} data={formatChartData()} />
      )}
    </div>
  );
};

export default StatsChart;
